import { SolText, type SolValidationMessage } from '@sol/components';

export enum AsmErrorType {
  /**
   * Network failure, status 0 (transport/offline/CORS).
   */
  NETWORK = 'NETWORK',
  /**
   * HTTP 400 Bad Request.
   */
  BAD_REQUEST = 'BAD_REQUEST',
  /**
   * HTTP 401 Unauthorized.
   */
  UNAUTHORIZED = 'UNAUTHORIZED',
  /**
   * HTTP 403 Forbidden.
   */
  FORBIDDEN = 'FORBIDDEN',
  /**
   * HTTP 404 Not Found.
   */
  NOT_FOUND = 'NOT_FOUND',
  /**
   * HTTP 405 Method Not Allowed.
   */
  METHOD_NOT_ALLOWED = 'METHOD_NOT_ALLOWED',
  /**
   * HTTP 410 Gone. The resource existed but is no longer retrievable, and
   * retrying will not change that.
   */
  GONE = 'GONE',
  /**
   * HTTP 429 Too Many Requests.
   */
  TOO_MANY_REQUESTS = 'TOO_MANY_REQUESTS',
  /**
   * HTTP 500 Internal Server Error.
   */
  INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
  /**
   * HTTP 503 Service Unavailable.
   */
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
}

export type AsmDetailErrorKind = 'not-found' | 'forbidden' | 'error';

export class AsmError {
  private constructor(
    public readonly type: AsmErrorType,
    public readonly status: number,
    public readonly messages: SolValidationMessage[],
    public readonly operation: string,
  ) {}

  public static of(
    type: AsmErrorType,
    status: number,
    messages: SolValidationMessage[] = [],
    operation: string = '',
  ): AsmError {
    const finalMessages = AsmError.ensureMessages(messages);
    return new AsmError(type, status, finalMessages, operation ?? '');
  }

  public firstMessage(): SolValidationMessage {
    return this.messages[0];
  }

  public toFirstMessageText(): SolText {
    const message =
      this.messages.find(msg => msg.path === undefined || msg.path === '') ??
      this.firstMessageForPath('') ??
      this.firstMessage();
    return message.text;
  }

  public messagesForPath(path: string): SolValidationMessage[] {
    return this.messages.filter(msg => msg.path === path);
  }

  public firstMessageForPath(path: string): SolValidationMessage | undefined {
    const matches = this.messagesForPath(path);
    return matches.length === 0 ? undefined : matches[0];
  }

  public get message(): string {
    return this.toFirstMessageText().text;
  }

  public toDetailErrorKind(): AsmDetailErrorKind {
    if (this.type === AsmErrorType.NOT_FOUND) {
      return 'not-found';
    }
    if (this.type === AsmErrorType.FORBIDDEN) {
      return 'forbidden';
    }
    return 'error';
  }

  public toString(): string {
    const statusPart = Number.isFinite(this.status) ? ` ${this.status}` : '';
    const header = `[${this.type}${statusPart}]${this.operation ? ' ' + this.operation : ''}`;
    const body = this.messages
      .map(msg => {
        const path = msg.path ? `${msg.path}: ` : '';
        return `${path}${msg.text.text}`;
      })
      .join('; ');
    return `${header} ${body}`;
  }

  private static ensureMessages(messages: SolValidationMessage[] | undefined): SolValidationMessage[] {
    if (messages === undefined || messages.length === 0) {
      return [{ type: 'error', text: SolText.of('asm-components.error') }];
    }
    return messages;
  }
}
